import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import type { GroupView } from "@starsach/shared-types";
import { useAccess, useCreateGroup, useGroups, useOrganisationUsers, useRoles, useUpdateGroup } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { useStepUp } from "../../auth/StepUpProvider";
import { useVisibleEnvironments } from "../../components/Environment";
import { NotHeld } from "../../components/NotHeld";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { ModulePage } from "../modules/ModulePage";
import { orderRoles } from "./parts";

type Draft = { name: string; roleIds: Set<string>; memberUserIds: Set<string>; environmentIds: Set<string> };

/**
 * A group's roles, users and environments (ADR-0017 section 2), on a page
 * of its own rather than a dialog (#426): an organization of 300 people
 * is a list to filter and scroll, and a page keeps each table's filter
 * and heads frozen under the header while it scrolls, has an address,
 * and goes back where it came from.
 */
export function GroupPage() {
  const { groupId = "new" } = useParams();
  const isNew = groupId === "new";
  return (
    <ModulePage module="access" section="groups" title={isNew ? "Access · New group" : "Access · Edit group"}>
      {(_d, environmentId) => <GroupEditor environmentId={environmentId} groupId={isNew ? undefined : groupId} />}
    </ModulePage>
  );
}

function GroupEditor({ environmentId, groupId }: { environmentId: string; groupId: string | undefined }) {
  const { me } = useAuth();
  const access = useAccess();
  const groups = useGroups();
  const back = `/access/${environmentId}/groups`;
  // ADR-0013: groups are the customer's; staff outside their portal are pointed there by the Groups section.
  if (me?.role === "internal-team" && !me.viewAs) return <Navigate to={back} replace />;
  const verb = groupId ? "roles:edit" : "roles:create";
  if (!access.isPending && !access.can(environmentId, verb)) {
    return (
      <Card>
        <NotHeld permission={verb} className="block" />
        <Link to={back} className="mt-3 inline-block text-sm underline">Back to groups</Link>
      </Card>
    );
  }
  if (groups.isPending) return <LoadingState label="Loading groups" />;
  if (groups.isError) return <ErrorState error={groups.error} onRetry={() => void groups.refetch()} />;
  const group = groupId ? groups.data.groups.find((g) => g.groupId === groupId) : undefined;
  if (groupId && !group) {
    return (
      <div className="grid gap-3">
        <EmptyState title="No such group" hint="It may have been deleted since the list was loaded." />
        <Link to={back} className="text-sm underline">Back to groups</Link>
      </div>
    );
  }
  return <GroupForm key={groupId ?? "new"} environmentId={environmentId} group={group} back={back} />;
}

function GroupForm({ environmentId, group, back }: { environmentId: string; group: GroupView | undefined; back: string }) {
  const navigate = useNavigate();
  const roles = useRoles();
  const envs = useVisibleEnvironments();
  const users = useOrganisationUsers();
  const create = useCreateGroup();
  const update = useUpdateGroup();
  const { ensureFresh } = useStepUp();
  const id = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Draft>(() =>
    group
      ? { name: group.name, roleIds: new Set(group.roleIds), memberUserIds: new Set(group.members.map((m) => m.userId)), environmentIds: new Set(group.environmentIds) }
      : { name: "", roleIds: new Set(), memberUserIds: new Set(), environmentIds: new Set([environmentId]) },
  );
  // #420's rule, kept on a page: the form starts on its first field.
  useEffect(() => nameRef.current?.focus(), []);

  const toggle = (key: "roleIds" | "memberUserIds" | "environmentIds") => (value: string, on: boolean) =>
    setDraft((d) => {
      const next = new Set(d[key]);
      if (on) next.add(value);
      else next.delete(value);
      return { ...d, [key]: next };
    });

  const pending = create.isPending || update.isPending;
  const error = create.isError ? create.error : update.isError ? update.error : undefined;
  const save = () => {
    const body = { name: draft.name.trim(), roleIds: [...draft.roleIds], memberUserIds: [...draft.memberUserIds], environmentIds: [...draft.environmentIds] };
    void ensureFresh().then(() => {
      const done = { onSuccess: () => navigate(back) };
      if (group) update.mutate({ groupId: group.groupId, ...body }, done);
      else create.mutate(body, done);
    }, () => undefined);
  };

  const roleRows = orderRoles(roles.data?.roles ?? []).map((r) => ({ id: r.roleId, label: r.name, description: r.description ?? "", kind: r.builtIn ? "built in" : "your own" }));
  const userRows = (users.data?.users ?? []).filter((u) => !u.removedAt).map((u) => ({ id: u.userId, label: u.displayName || (u.email ?? u.userId), email: u.email ?? u.userId, status: u.disabledAt ? "disabled" : u.lastSeenAt ? "active" : "invited" }));
  const envRows = (envs.data?.customers ?? []).map((e) => ({ id: e.environmentId, label: e.friendlyName, account: e.awsAccountNumber }));
  const noRole = draft.roleIds.size === 0;

  return (
    <form
      aria-label={group ? `Edit ${group.name}` : "New group"}
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <Card>
        <label htmlFor={`${id}-name`} className="block max-w-md text-sm">
          <span className="font-medium">Name</span>
          <input ref={nameRef} id={`${id}-name`} className={inputClass} value={draft.name} maxLength={60} required onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </label>
      </Card>
      <ChoiceTable
        title="Roles"
        caption="roles the group gives"
        rows={roleRows}
        loading={roles.isPending}
        error={roles.isError ? roles.error : undefined}
        chosen={draft.roleIds}
        onToggle={toggle("roleIds")}
        columns={[
          { key: "name", header: "Role", render: (r) => <span className="font-medium">{r.label}</span> },
          { key: "kind", header: "Kind", render: (r) => <span className="whitespace-nowrap"><StatusPill status={r.kind} tone="neutral" /></span> },
          { key: "description", header: "What it allows", render: (r) => <span className="text-neutral-600 dark:text-neutral-400">{r.description}</span> },
        ]}
        emptyAll={<EmptyState title="No roles to choose from" />}
      />
      <ChoiceTable
        title="Users"
        caption="users in the group"
        rows={userRows}
        loading={users.isPending}
        error={users.isError ? users.error : undefined}
        chosen={draft.memberUserIds}
        onToggle={toggle("memberUserIds")}
        sortBy={(u) => u.label}
        columns={[
          { key: "name", header: "Person", render: (u) => <span className="font-medium">{u.label}</span> },
          { key: "email", header: "Email", render: (u) => u.email },
          { key: "status", header: "Status", render: (u) => <StatusPill status={u.status} tone={u.status === "disabled" ? "warning" : u.status === "active" ? "ok" : "neutral"} /> },
        ]}
        emptyAll={<EmptyState title="Nobody to add yet" hint="Invite people from Access › People first." />}
      />
      <ChoiceTable
        title="Environments"
        caption="environments the roles apply on"
        rows={envRows}
        loading={envs.isPending}
        error={envs.isError ? envs.error : undefined}
        chosen={draft.environmentIds}
        onToggle={toggle("environmentIds")}
        sortBy={(e) => e.label}
        columns={[
          { key: "name", header: "Environment", render: (e) => <span className="font-medium">{e.label}</span> },
          { key: "account", header: "AWS account", render: (e) => e.account },
        ]}
        emptyAll={<EmptyState title="No environments to choose from" />}
      />
      {error !== undefined && <ErrorState error={error} lead="Couldn’t save the group." />}
      <div className="sticky bottom-0 z-[3] flex flex-wrap items-center gap-3 border-t border-neutral-200 bg-canvas py-3 dark:border-neutral-800 dark:bg-neutral-950">
        <button type="submit" className={btnPrimary} disabled={pending || !draft.name.trim() || noRole} aria-describedby={noRole ? `${id}-why` : undefined}>
          {pending ? "Saving…" : group ? "Save group" : "Create group"}
        </button>
        <Link to={back} className={btnSecondary}>Cancel</Link>
        <span className="text-sm text-neutral-600 dark:text-neutral-400">
          {count(draft.roleIds.size, "role")} · {count(draft.memberUserIds.size, "user")} · {count(draft.environmentIds.size, "environment")}
        </span>
        {noRole && (
          <span id={`${id}-why`} className="text-sm text-neutral-600 dark:text-neutral-400">
            A group needs at least one role.
          </span>
        )}
      </div>
    </form>
  );
}

const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

type Row = { id: string; label: string };

/**
 * One of the three lists: a DataTable with its own filter, a checkbox per
 * row, how many are chosen, and "Show chosen only" to review a long list.
 * The choice lives in the draft, not the table, so it survives a filter.
 */
function ChoiceTable<T extends Row>({
  title,
  caption,
  rows,
  loading,
  error,
  chosen,
  onToggle,
  columns,
  sortBy,
  emptyAll,
}: {
  title: string;
  caption: string;
  rows: T[];
  loading: boolean;
  error: unknown;
  chosen: Set<string>;
  onToggle: (id: string, on: boolean) => void;
  columns: Array<{ key: string; header: ReactNode; render: (row: T) => ReactNode }>;
  sortBy?: (row: T) => string;
  emptyAll: ReactNode;
}) {
  const [chosenOnly, setChosenOnly] = useState(false);
  const headingId = useId();
  const onlyId = useId();
  const shown = chosenOnly ? rows.filter((r) => chosen.has(r.id)) : rows;
  const picked = rows.filter((r) => chosen.has(r.id)).length;
  return (
    <Card>
      <div role="group" aria-labelledby={headingId}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
        <h2 id={headingId} className="text-base font-semibold text-ink dark:text-white">
          {title}
        </h2>
        <span className="text-sm text-neutral-600 dark:text-neutral-400" aria-live="polite">
          {picked} of {rows.length} chosen
        </span>
        <label htmlFor={onlyId} className="flex items-center gap-2 text-sm">
          <input id={onlyId} type="checkbox" checked={chosenOnly} onChange={(e) => setChosenOnly(e.target.checked)} />
          Show chosen only
        </label>
      </div>
      {loading ? (
        <LoadingState label={`Loading ${title.toLowerCase()}`} />
      ) : error !== undefined ? (
        <ErrorState error={error} />
      ) : (
        <DataTable
          caption={`${title}: ${caption}`}
          rows={shown}
          rowKey={(r) => r.id}
          {...(sortBy ? { sortBy } : {})}
          empty={rows.length === 0 ? emptyAll : <EmptyState title={`No ${title.toLowerCase()} chosen yet`} hint="Clear Show chosen only to see them all." />}
          columns={[
            {
              key: "choose",
              header: <span className="sr-only">Chosen</span>,
              render: (r) => <input type="checkbox" aria-label={`Choose ${r.label}`} checked={chosen.has(r.id)} onChange={(e) => onToggle(r.id, e.target.checked)} />,
            },
            ...columns,
          ]}
        />
      )}
      </div>
    </Card>
  );
}
